import React, { useMemo } from "react";
import { Checkbox, Pagination, Tooltip, Typography, Empty } from "antd";
import { DownOutlined as DownIcon, UpOutlined as UpIcon } from "@ant-design/icons";
import { formatMoney } from "./utils";
import SummarySearchBar from "./SummarySearchBar";

const { Text } = Typography;

function SelectedSpecificationsSummary({
  showSummarySection,
  summaryExpanded,
  setSummaryExpanded,
  allSummarySelected,
  summarySelectedKeys = [],
  setSummarySelectedKeys,
  summaryRows = [],
  paginatedSummaryRows,
  summaryPage,
  summaryPageSize,
  setSummaryPage,
  setSummaryPageSize,
  summarySearchQuery,
  onSearchQueryChange,
  summaryTotal,
}) {
  const selectedKeysSet = useMemo(
    () => new Set(summarySelectedKeys),
    [summarySelectedKeys],
  );

  if (!showSummarySection) return null;

  const summaryRowsToRender = paginatedSummaryRows || summaryRows;

  return (
    <section className="optimisation-review__section optimisation-review__card">
      <div className="optimisation-review__summary-header">
        <div className="optimisation-review__summary-header-left">
          <button
            type="button"
            className="optimisation-review__summary-title-btn"
            onClick={() => setSummaryExpanded((prev) => !prev)}
          >
            <Text strong>Selected Specifications Summary</Text>
          </button>
          {summaryExpanded && (
            <div className="optimisation-review__summary-actions">
              <SummarySearchBar
                value={summarySearchQuery}
                onChange={onSearchQueryChange}
                placeholder="Search by Spec or Card Number"
              />
            </div>
          )}
        </div>
        <button
          type="button"
          className="optimisation-review__summary-chevron-btn"
          onClick={() => setSummaryExpanded((prev) => !prev)}
          aria-label={summaryExpanded ? "Collapse" : "Expand"}
        >
          {summaryExpanded ? <UpIcon /> : <DownIcon />}
        </button>
      </div>

      {summaryExpanded && (
        <div className="optimisation-review__summary-content">
          <div className="optimisation-review__summary-table-wrap">
            <table className="optimisation-review__summary-table">
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      className="optimisation-review__summary-checkbox"
                      checked={allSummarySelected}
                      indeterminate={
                        summarySelectedKeys.length > 0 && !allSummarySelected
                      }
                      onChange={(event) => {
                        setSummarySelectedKeys(
                          event.target.checked
                            ? summaryRows.map((row) => row.key)
                            : [],
                        );
                      }}
                    />
                  </th>
                  <th>Spec</th>
                  <th>Card<br />Number</th>
                  <th>Compliance</th>
                  <th>Current<br />Interval</th>
                  <th>Optimised<br />Interval</th>
                  <th>Applied<br />Optimization</th>
                  <th>Applied<br />Yield</th>
                  <th>Projected<br />Savings</th>
                  <th>Estimated<br />Savings</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {summaryRowsToRender.length > 0 ? (
                  summaryRowsToRender.map((item) => {
                    const checked = selectedKeysSet.has(item.key);
                    return (
                      <tr
                        key={item.key}
                        className={
                          checked
                            ? "optimisation-review__summary-row--selected"
                            : undefined
                        }
                      >
                        <td>
                          <Checkbox
                            className="optimisation-review__summary-checkbox"
                            checked={checked}
                            onChange={(event) => {
                              const isChecked = event.target.checked;
                              setSummarySelectedKeys((prev) =>
                                isChecked
                                  ? [...prev, item.key]
                                  : prev.filter((key) => key !== item.key),
                              );
                            }}
                          />
                        </td>
                        <td>
                          <Tooltip
                            title={item.taskDescription || "No task description"}
                            color="#ffffff"
                            overlayClassName="optimisation-review__spec-tooltip-overlay"
                          >
                            <span className="optimisation-review__spec-tooltip">
                              {item.spec}
                            </span>
                          </Tooltip>
                        </td>
                        <td>{item.cardNo}</td>
                        <td>{item.compliance}</td>
                        <td>{item.currentInterval}</td>
                        <td>{item.optimisedInterval}</td>
                        <td>{item.appliedOptimization}</td>
                        <td>{item.appliedYield}</td>
                        <td className="optimisation-review__summary-savings">
                          {formatMoney(item.projectedSavings)}
                        </td>
                        <td>{formatMoney(item.estimatedSavings)}</td>
                        <td>
                          <span
                            className={`optimisation-review__status-badge${
                              item.status === "Improved"
                                ? " optimisation-review__status-badge--improved"
                                : item.status === "Degraded"
                                  ? " optimisation-review__status-badge--degraded"
                                  : ""
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} style={{ padding: "40px 0", textAlign: "center" }}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Records Found" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {summaryTotal > 0 && (
              <div className="optimisation-review__summary-pagination">
                <Pagination
                  current={summaryPage}
                  pageSize={summaryPageSize}
                  total={summaryTotal}
                  showSizeChanger
                  pageSizeOptions={[10, 20, 50]}
                  onChange={(page, size) => {
                    setSummaryPage(page);
                    setSummaryPageSize(size);
                  }}
                  onShowSizeChange={(_current, size) => {
                    setSummaryPage(1);
                    setSummaryPageSize(size);
                  }}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} specifications`
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default React.memo(SelectedSpecificationsSummary);
